import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useState } from 'react';
import {
  requiredDocumentTemplates,
  type RequiredDocumentTemplate, type RequiredDocumentTemplateInput,
  type DocumentSource,
} from '@/api/endpoints';
import { Icon } from '@/components/Icon';
import { Pagination, usePaginated } from '@/components/Pagination';
import { SearchableSelect } from '@/components/SearchableSelect';

/** Lista typów opracowań — kody zgodne z backendowym StudyType.name(). */
const STUDY_TYPES = [
  { code: 'OP_WOD_3B', label: '3b — Operat w-p (pobór wód)' },
  { code: 'OP_WOD_3A', label: '3a — Operat w-p (urządzenie wodne)' },
  { code: 'OP_WOD_3C', label: '3c — Operat w-p (odprowadzenie)' },
  { code: 'PRG_1A',    label: '1a — PRG (wykonanie otworu)' },
  { code: 'PRG_1B',    label: '1b — PRG (likwidacja)' },
  { code: 'PRG_1C',    label: '1c — PRG (rekonstrukcja)' },
  { code: 'KIP_2',     label: '2 — Karta informacyjna przedsięwzięcia' },
  { code: 'DOK_HYDRO_4A', label: '4a — Dok. hydrogeol. (zasoby eksploatacyjne)' },
  { code: 'DOK_HYDRO_4B', label: '4b — Dok. hydrogeol. (wydajność otworów)' },
  { code: 'DOK_HYDRO_4C', label: '4c — Dok. hydrogeol. (strefy ochronne)' },
  { code: 'DOK_LIKW_5',   label: '5 — Dok. geol. z likwidacji otworu' },
  { code: 'ANALIZA_RYZYKA_6', label: '6 — Analiza ryzyka' },
  { code: 'PLAN_BEZP_7',      label: '7 — Plan bezpieczeństwa wody' },
  { code: 'PLAN_RUCHU_8',     label: '8 — Plan ruchu' },
];

const SOURCES: { value: DocumentSource; label: string }[] = [
  { value: 'FIRM',   label: 'Firma' },
  { value: 'CLIENT', label: 'Klient' },
];

/**
 * Admin: słownik wymaganych dokumentów per typ opracowania. Definiuje listę pozycji checklisty,
 * które są automatycznie kopiowane do każdego nowego Study tego typu.
 */
export function RequiredDocsAdmin() {
  const [studyType, setStudyType] = useState<string>('OP_WOD_3B');
  const [rows, setRows] = useState<RequiredDocumentTemplate[] | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<RequiredDocumentTemplate | null>(null);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reload = () => {
    setErr(null);
    setRows(null);
    requiredDocumentTemplates.list(studyType, includeInactive)
      .then(setRows)
      .catch(e => setErr(e?.message ?? String(e)));
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [studyType, includeInactive]);

  const { pagedItems, page, pageSize, setPage, setPageSize, total } = usePaginated(rows ?? [], { storageKey: 'required-docs' });

  const onDeactivate = async (t: RequiredDocumentTemplate) => {
    if (!confirm(`Dezaktywować pozycję „${t.code} — ${t.name}"?`)) return;
    try {
      await requiredDocumentTemplates.delete(t.id);
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="max-w-[1500px]">
      <header className="flex items-end justify-between mb-5 gap-4 flex-wrap">
        <div>
          <h1>Wymagane dokumenty wejściowe</h1>
          <div className="text-ink-400 text-[13px]">
            Słownik pozycji checklisty per typ opracowania. Każde nowe opracowanie tego typu
            dostaje automatycznie kopię tej listy (geolog odznacza, co już ma).
          </div>
        </div>
        <div className="flex gap-2 items-end">
          <label className="text-[12px] text-ink-700 flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={includeInactive} onChange={e => setIncludeInactive(e.target.checked)}/>
            Pokaż dezaktywowane
          </label>
          <button onClick={() => setCreating(true)} className="btn btn-primary btn-sm">
            <Icon name="plus" className="w-3.5 h-3.5"/> Nowa pozycja
          </button>
        </div>
      </header>

      <div className="mb-3 flex items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">Typ opracowania:</span>
        <SearchableSelect value={studyType} onChange={setStudyType} className="max-w-[380px]">
          {STUDY_TYPES.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
        </SearchableSelect>
      </div>

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}

      <div className="card">
        <div className="card-body !p-0">
          {!rows && <div className="p-4 text-ink-400">Ładowanie…</div>}
          {rows && rows.length === 0 && (
            <div className="p-4 text-ink-400">
              Brak pozycji dla {studyType}. Dodaj pierwszą lub wybierz inny typ opracowania.
            </div>
          )}
          {rows && rows.length > 0 && (
            <>
            <table className="table-hw">
              <thead>
                <tr>
                  <th className="w-20">Sort</th>
                  <th>Kod</th>
                  <th>Nazwa</th>
                  <th className="w-24">Źródło</th>
                  <th className="w-20">Obow.</th>
                  <th className="w-32">Próg Q (m³/rok)</th>
                  <th>Podstawa prawna</th>
                  <th className="w-24">Status</th>
                  <th className="w-20"></th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map(t => (
                  <tr key={t.id} className={t.active ? '' : 'opacity-50'}>
                    <td className="font-mono text-[11px] text-ink-500">{t.sortOrder}</td>
                    <td className="font-mono text-[11px] text-ink-800">{t.code}</td>
                    <td className="text-ink-900 max-w-[320px]">
                      <div>{t.name}</div>
                      {t.conditionText && (
                        <div className="text-[11px] text-amber-700 mt-0.5">⚠ {t.conditionText}</div>
                      )}
                    </td>
                    <td>
                      <span className={t.defaultSource === 'FIRM' ? 'pill pill-green' : 'pill'}>
                        {t.defaultSource === 'FIRM' ? 'Firma' : 'Klient'}
                      </span>
                    </td>
                    <td>{t.mandatory
                      ? <span className="pill pill-amber">tak</span>
                      : <span className="text-ink-400 text-[11px]">opcjonalna</span>}</td>
                    <td className="font-mono text-[11px] text-ink-700">
                      {t.qThresholdM3Year != null ? t.qThresholdM3Year.toLocaleString('pl-PL') : '—'}
                    </td>
                    <td className="text-[11px] text-ink-500 max-w-[180px] truncate" title={t.legalBasis ?? ''}>
                      {t.legalBasis ?? '—'}
                    </td>
                    <td>{t.active
                      ? <span className="pill pill-green">aktywna</span>
                      : <span className="pill pill-amber">dezakt.</span>}</td>
                    <td className="text-right whitespace-nowrap">
                      <button onClick={() => setEditing(t)} className="text-water-600 hover:text-water-800 p-1" title="Edytuj">
                        <Icon name="edit" className="w-3.5 h-3.5"/>
                      </button>
                      {t.active && (
                        <button onClick={() => onDeactivate(t)} className="text-red-500 hover:text-red-700 p-1 ml-1" title="Dezaktywuj">
                          <Icon name="x" className="w-3.5 h-3.5"/>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-3 border-t border-ink-100">
              <Pagination total={total} page={page} pageSize={pageSize}
                          onPageChange={setPage} onPageSizeChange={setPageSize}/>
            </div>
            </>
          )}
        </div>
      </div>

      {(creating || editing) && (
        <RequiredDocEditModal
          studyType={studyType}
          initial={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); reload(); }}
        />
      )}
    </div>
  );
}

function RequiredDocEditModal({ studyType, initial, onClose, onSaved }: {
  studyType: string;
  initial: RequiredDocumentTemplate | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = initial != null;
  const [form, setForm] = useState<RequiredDocumentTemplateInput>({
    studyType: initial?.studyType ?? studyType,
    code: initial?.code ?? '',
    name: initial?.name ?? '',
    description: initial?.description ?? '',
    legalBasis: initial?.legalBasis ?? '',
    defaultSource: initial?.defaultSource ?? 'CLIENT',
    mandatory: initial?.mandatory ?? true,
    conditionText: initial?.conditionText ?? '',
    qThresholdM3Year: initial?.qThresholdM3Year ?? null,
    sortOrder: initial?.sortOrder ?? 100,
    active: initial?.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (!form.code.trim()) { setErr('Kod jest wymagany'); return; }
    if (!form.name.trim()) { setErr('Nazwa jest wymagana'); return; }
    setBusy(true); setErr(null);
    try {
      if (isEdit && initial) await requiredDocumentTemplates.update(initial.id, form);
      else await requiredDocumentTemplates.create(form);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: form, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-auto" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <div className="bg-white rounded-lg shadow-2xl max-w-[700px] w-full mt-8" onClick={e => e.stopPropagation()}>
        <div className="border-b border-ink-100 px-5 py-3 flex items-center gap-3">
          <h2 className="text-[16px] font-semibold m-0">
            {isEdit ? `Edycja pozycji ${initial?.code}` : 'Nowa pozycja słownika'}
          </h2>
          <button onClick={requestClose} className="ml-auto text-ink-400 hover:text-ink-700"><Icon name="x"/></button>
        </div>

        <div className="p-5 space-y-3 max-h-[80vh] overflow-y-auto">
          {err && <div className="text-red-500 text-[12px]">{err}</div>}

          <div className="grid grid-cols-3 gap-3">
            <Field label="Typ opracowania*">
              <SearchableSelect value={form.studyType} onChange={v => setForm({ ...form, studyType: v })}
                      disabled={isEdit}>
                {STUDY_TYPES.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
              </SearchableSelect>
            </Field>
            <Field label="Kod (np. DOK_HYDRO)*">
              <input value={form.code}
                     onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                     className="input font-mono"/>
            </Field>
            <Field label="Kolejność sortowania">
              <input type="number" value={form.sortOrder ?? 100}
                     onChange={e => setForm({ ...form, sortOrder: Number(e.target.value) })}
                     className="input font-mono"/>
            </Field>
          </div>

          <Field label="Nazwa*">
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="input"/>
          </Field>

          <Field label="Opis">
            <textarea value={form.description ?? ''}
                      onChange={e => setForm({ ...form, description: e.target.value })}
                      className="input min-h-[60px]" rows={3}/>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Domyślne źródło*">
              <SearchableSelect value={form.defaultSource}
                      onChange={v => setForm({ ...form, defaultSource: v as DocumentSource })}>
                {SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </SearchableSelect>
            </Field>
            <Field label="Podstawa prawna">
              <input value={form.legalBasis ?? ''}
                     onChange={e => setForm({ ...form, legalBasis: e.target.value })}
                     placeholder="np. Art. 407 ust. 4 PrW" className="input"/>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Próg Q rocznego (m³/rok)">
              <input type="number" value={form.qThresholdM3Year ?? ''}
                     onChange={e => setForm({ ...form, qThresholdM3Year: e.target.value ? Number(e.target.value) : null })}
                     placeholder="np. 1000000 — pomija pozycję gdy roczny pobór niższy"
                     className="input font-mono"/>
            </Field>
            <Field label="Warunek (tekstowy)">
              <input value={form.conditionText ?? ''}
                     onChange={e => setForm({ ...form, conditionText: e.target.value })}
                     placeholder="np. Tylko jeśli decyzja środowiskowa była wymagana" className="input"/>
            </Field>
          </div>

          <div className="flex items-center gap-5">
            <label className="flex items-center gap-2 text-[12px] text-ink-700 cursor-pointer">
              <input type="checkbox" checked={form.mandatory ?? true}
                     onChange={e => setForm({ ...form, mandatory: e.target.checked })}/>
              Obowiązkowa
            </label>
            {isEdit && (
              <label className="flex items-center gap-2 text-[12px] text-ink-700 cursor-pointer">
                <input type="checkbox" checked={form.active ?? true}
                       onChange={e => setForm({ ...form, active: e.target.checked })}/>
                Aktywna (widoczna w nowych checklistach)
              </label>
            )}
          </div>
        </div>

        <div className="border-t border-ink-100 px-5 py-3 flex justify-end gap-2">
          <button onClick={requestClose} disabled={busy} className="btn btn-secondary btn-sm">Anuluj</button>
          <button onClick={save} disabled={busy} className="btn btn-primary btn-sm">
            {busy ? 'Zapisywanie…' : (isEdit ? 'Zapisz zmiany' : 'Dodaj pozycję')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">{label}</span>
      {children}
    </div>
  );
}
